import { AsyncPipe, NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Banner, textboxPlacementClass } from 'src/app/models';
import { ContentService } from 'src/app/services/content/content.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { symbolIcons } from '../../../modules/icon.module';
import { IconButtonComponent, TextButtonComponent } from '../button';

type Direction = 'prev' | 'next';

@Component({
  selector: 'app-hero-banner',
  templateUrl: './hero-banner.component.html',
  styleUrls: ['./hero-banner.component.scss'],
  host: {
    '[style.--banner-height]': 'bannerHeight',
    '[style.--banner-max-height]': 'bannerMaxHeight',
    '[style.--banner-mobile-height]': 'bannerMobileHeight',
    '[style.--banner-mobile-max-height]': 'bannerMobileMaxHeight',
    '[style.--banner-brightness]': 'bannerbrightness',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgTemplateOutlet, AsyncPipe, IconButtonComponent, RouterLink, TextButtonComponent, MatIconModule, NgClass, NgOptimizedImage],
})
export class HeroBannerComponent implements OnInit, AfterContentInit, OnDestroy {
  private renderer = inject(Renderer2);
  private cdr = inject(ChangeDetectorRef);
  private contentService = inject(ContentService);
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);
  private deviceService = inject(DeviceDetectorService);
  private ngZone = inject(NgZone);

  private subscription = new Subscription();
  arrowIcon = symbolIcons.left_expand_arrow;
  textboxPlacementClass = textboxPlacementClass;

  @Input() activeBanners: Banner[];
  bannerHeight: string;
  bannerMaxHeight: string;
  bannerMobileHeight: string;
  bannerMobileMaxHeight: string;
  bannerbrightness: number;

  animationTimeout = 750;
  manualOperationTimeout = 10000;
  currentSlide: BehaviorSubject<number>;
  animating: boolean;
  manual: boolean;
  timerId: any;

  @ViewChild('slides') slidesElement: ElementRef;
  @ViewChild('player') player: ElementRef;

  isMobile: boolean;

  async ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.setupBanner();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  sameSite(link: string): string {
    return this.contentService.sameSite({
      type: 'link',
      titleText: null,
      targetUrl: link,
      new: null,
    })?.targetUrl;
  }

  setupBanner() {
    this.animating = false;

    this.currentSlide = new BehaviorSubject(0);
    if (typeof window !== 'undefined') {
      if (this.activeBanners.length > 1) {
        this.subscription.add(
          this.currentSlide.subscribe((slide) => {
            const slideContent = this.activeBanners[slide];

            this.bannerHeight = slideContent.height;
            this.bannerMaxHeight = slideContent.maxHeight;
            this.bannerMobileHeight = slideContent.mobileHeight;
            this.bannerMobileMaxHeight = slideContent.mobileMaxHeight;
            this.bannerbrightness = slideContent.brightness;

            if (slideContent.displayTimeSeconds) {
              let displayTime = slideContent.displayTimeSeconds * 1000;
              if (this.manual) {
                displayTime = this.manualOperationTimeout;
                this.manual = false;
              }

              this.timeoutSelect('next', displayTime);
            }
          }),
        );
      }
    }
    if (this.activeBanners.length === 1 || (typeof window === 'undefined' && this.activeBanners.length > 0)) {
      const slideContent = this.activeBanners[0];

      this.bannerHeight = slideContent.height;
      this.bannerMaxHeight = slideContent.maxHeight;
      this.bannerMobileHeight = slideContent.mobileHeight;
      this.bannerMobileMaxHeight = slideContent.mobileMaxHeight;
      this.bannerbrightness = slideContent.brightness;
    }
  }

  get prevSlide(): number {
    return this.mod(this.currentSlide.value - 1, this.activeBanners.length);
  }

  get nextSlide(): number {
    return this.mod(this.currentSlide.value + 1, this.activeBanners.length);
  }

  resetPlayer() {
    if (!this.player) {
      return;
    }
  }

  async selectSlide(direction: Direction, manual = true) {
    this.manual = manual;
    if (this.animating) {
      this.timeoutSelect(direction);

      return;
    }

    this.player?.nativeElement.pause();
    await this.animateSlide(direction);
    if (this.player) this.player.nativeElement.currentTime = 0;
    this.player?.nativeElement.play();

    if (direction === 'next') {
      this.currentSlide.next(this.nextSlide);
    } else if (direction === 'prev') {
      this.currentSlide.next(this.prevSlide);
    }
  }

  private timeoutSelect(direction: Direction, timeout = this.animationTimeout) {
    this.ngZone.runOutsideAngular(() => {
      clearTimeout(this.timerId);
      this.timerId = setTimeout(() => {
        this.selectSlide(direction, false);
      }, timeout);
    });
  }

  private async animateSlide(direction: Direction) {
    this.animating = true;
    this.cdr.detectChanges();

    return new Promise<void>((resolve) => {
      const bannerWidth: number = this.slidesElement.nativeElement.offsetWidth;

      this.renderer.setStyle(this.slidesElement.nativeElement, 'transition', `all ${this.animationTimeout}ms ease-out`);
      if (direction === 'next') {
        this.renderer.setStyle(this.slidesElement.nativeElement, 'transform', `translateX(-${bannerWidth}px)`);
      } else {
        this.renderer.setStyle(this.slidesElement.nativeElement, 'transform', `translateX(${bannerWidth}px)`);
      }

      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.renderer.setStyle(this.slidesElement.nativeElement, 'transition', '');
          this.renderer.setStyle(this.slidesElement.nativeElement, 'transform', '');

          this.animating = false;
          this.cdr.detectChanges();

          resolve();
        }, this.animationTimeout);
      });
    });
  }

  private mod(n: number, m: number): number {
    return ((n % m) + m) % m;
  }
}
